import { UPLOAD } from '@/constants';
import useEventListener from '@/hooks/useEventListener';
import { isEnableSaveMediaSelector } from '@/redux/features/trackingPageMedia.slice';
import { DropZone, Icon, InlineError, Text } from '@shopify/polaris';
import { ImageWithTextOverlayIcon } from '@shopify/polaris-icons';
import { memo, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { validImageTypes } from './config';
import { UploadFileStyled } from './styled';

interface Props {
  onSave: (file: File) => void;
  label: string;
  children?: any;
  url: string;
  percentByHeight?: number;
  placeholderText?: React.ReactNode;
  labelHidden?: boolean;
  errorMessage?: string;
  disabled?: boolean;
}

function UploadFile({ ...props }: Props) {
  const isEnableSaveMedia = useSelector(isEnableSaveMediaSelector);

  const [url, setUrl] = useState('');
  const [error, setError] = useState<string>('');
  const [width, setWidth] = useState<number>(0);
  const squareRef = useRef<HTMLInputElement>(null);
  const handleDropZoneDrop = (_dropFiles: File[], acceptedFiles: File[], _rejectedFiles: File[]) => {
    if (acceptedFiles.length !== 0) {
      const file = acceptedFiles[0];
      const src = window.URL.createObjectURL(file);
      setUrl(src);
      setError('');
      props.onSave(file);
    }
  };

  const validateFile = (file: File) => {
    if (file.size > UPLOAD.maxSize) {
      setError(props.errorMessage ?? 'File too large');
      setUrl(props.url);
    }
    if (!validImageTypes.includes(file.type)) {
      setError('Unsupported format');
      setUrl(props.url);
    }
    return file.size < UPLOAD.maxSize && validImageTypes.includes(file.type);
  };

  const uploadedFiles = url ? (
    <img className="preview-image" src={url} alt="_blank" width="100%" height="100%" loading="lazy" />
  ) : null;

  const handleSize = () => {
    setWidth(squareRef.current?.offsetWidth || 0);
  };

  useEventListener('resize', handleSize);

  // first load or click discard on modal tracking page
  useEffect(() => {
    if (isEnableSaveMedia) return;
    const versionImage = `?v=${Date.now()}`;
    setUrl(props.url + versionImage);
  }, [isEnableSaveMedia, props.url]);

  useEffect(() => {
    handleSize();
  }, []);

  return (
    <UploadFileStyled height={props.percentByHeight ? `${props.percentByHeight * width}px` : '9rem'}>
      <DropZone
        customValidator={validateFile}
        label={props.label}
        labelHidden={props.labelHidden}
        onDrop={handleDropZoneDrop}
        variableHeight
        accept={validImageTypes.join(',')}
        type="image"
        disabled={props.disabled}
      >
        <div className="border-frame" ref={squareRef}>
          <div className="preview-text">
            {props.placeholderText ? (
              props.placeholderText
            ) : (
              <>
                <Icon source={ImageWithTextOverlayIcon} tone="base" />
                <Text as="h6" variant="bodyMd">
                  Upload
                </Text>
              </>
            )}
          </div>
          {uploadedFiles}
        </div>
      </DropZone>
      {error && (
        <div className="mt-8">
          <InlineError message={error} fieldID="upload-image" />
        </div>
      )}
      {props.children}
    </UploadFileStyled>
  );
}

export default memo(UploadFile);
